import React from "react";
import CohortFilter, { COHORT } from "./CohortFilter";
import "./ReportWorkspace.css";

const STATUS_OPTIONS = {
  loans: ["submitted", "booked", "initiated", "paid", "authorized", "rejected"],
  mart: ["initiated", "approved", "shipped", "rejected"],
};

const localDate = (date) => [
  date.getFullYear(),
  String(date.getMonth() + 1).padStart(2, "0"),
  String(date.getDate()).padStart(2, "0"),
].join("-");

function ReportWorkspace({
  title, reportType = "loans", onReportTypeChange,
  formData, setFormData, cohort, onCohortChange,
  isLoading, activeAction, onGenerate, onGenerateAll,
}) {
  const isMart = reportType === "mart";
  const cohortLabel = cohort === COHORT.STAFF ? "UPNMG Staff" : "Members";
  const invalidRange = Boolean(formData.fromDate && formData.toDate && formData.fromDate > formData.toDate);
  const today = new Date();
  const presets = [
    { label: "This month", from: new Date(today.getFullYear(), today.getMonth(), 1), to: today },
    { label: "Last month", from: new Date(today.getFullYear(), today.getMonth() - 1, 1), to: new Date(today.getFullYear(), today.getMonth(), 0) },
    { label: "This year", from: new Date(today.getFullYear(), 0, 1), to: today },
  ];

  const handleChange = ({ target: { name, value } }) => {
    setFormData((previous) => ({ ...previous, [name]: value }));
  };

  const resetFilters = () => {
    setFormData({ category: "", fromDate: "", toDate: "" });
    onCohortChange(COHORT.MEMBERS);
  };

  return (
    <section className="report-page" aria-label={title}>
      <header className="report-page__heading">
        <h2>{title}</h2>
        <span>Excel (.xlsx)</span>
      </header>

      <div className="report-page__panel">
        {onReportTypeChange && (
          <div className="report-page__tabs" role="group" aria-label="Report type">
            {[{ value: "loans", label: "Loans" }, { value: "mart", label: "Mart" }].map(({ value, label }) => (
              <button key={value} type="button" aria-label={value === "loans" ? "Loan reports" : "Mart reports"}
                aria-pressed={reportType === value} disabled={isLoading}
                onClick={() => onReportTypeChange(value)}>{label}</button>
            ))}
          </div>
        )}

        <form onSubmit={(event) => { event.preventDefault(); onGenerate(); }}>
          <div className="report-page__form-heading">
            <h3>Filters</h3>
            <button type="button" className="report-page__reset" onClick={resetFilters} disabled={isLoading}>Reset filters</button>
          </div>

          <fieldset className="report-page__fields" disabled={isLoading}>
            <legend className="sr-only">Report filters</legend>
            <div className="report-page__inputs">
              <CohortFilter value={cohort} onChange={onCohortChange} label="User type" />
              <div>
                <label htmlFor="category">{isMart ? "Order status" : "Loan status"}</label>
                <select id="category" name="category" value={formData.category} onChange={handleChange} required>
                  <option value="" disabled>Select status</option>
                  {STATUS_OPTIONS[reportType].map((status) => (
                    <option key={status} value={status}>{status.charAt(0).toUpperCase() + status.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="fromDate">From date</label>
                <input id="fromDate" type="date" name="fromDate" value={formData.fromDate}
                  max={formData.toDate || undefined} onChange={handleChange} required
                  aria-describedby={invalidRange ? "report-date-error" : undefined} aria-invalid={invalidRange} />
              </div>
              <div>
                <label htmlFor="toDate">To date</label>
                <input id="toDate" type="date" name="toDate" value={formData.toDate}
                  min={formData.fromDate || undefined} onChange={handleChange} required
                  aria-describedby={invalidRange ? "report-date-error" : undefined} aria-invalid={invalidRange} />
              </div>
            </div>

            <div className="report-page__presets" role="group" aria-label="Date range shortcuts">
              {presets.map(({ label, from, to }) => {
                const fromDate = localDate(from);
                const toDate = localDate(to);
                return (
                  <button key={label} type="button" aria-pressed={formData.fromDate === fromDate && formData.toDate === toDate}
                    onClick={() => setFormData((previous) => ({ ...previous, fromDate, toDate }))}>{label}</button>
                );
              })}
            </div>
            {invalidRange && <p id="report-date-error" className="report-page__error" role="alert">The end date must be on or after the start date.</p>}
          </fieldset>

          <div className="report-page__footer">
            <div className="report-page__actions">
              <button className="report-page__generate" type="submit" disabled={isLoading}>
                {isLoading && activeAction === "range" ? "Generating..." : "Generate report"}
              </button>
              <button className="report-page__generate-all" type="button" onClick={onGenerateAll} disabled={isLoading} aria-describedby="report-all-note">
                {isLoading && activeAction === "all" ? "Generating..." : "Generate all"}
              </button>
            </div>
            <p id="report-all-note">
              {isMart
                ? `Generate all includes every order status for ${cohortLabel} within the selected dates.`
                : `Generate all includes every loan for ${cohortLabel}, regardless of status or dates.`}
            </p>
          </div>
        </form>
      </div>
    </section>
  );
}

export default ReportWorkspace;
